import {
  type DrillProgress,
  drillStart,
  isFinished,
} from "../handwriting/xiezi-stages.js";
import { flashLength } from "./xiezi-flash.js";
import { withinTaste } from "../handwriting/xiezi-taste.js";
import {
  type ExerciseKeeper,
  keptAfterPass,
  nextUnfinished,
  settleAfterPass,
} from "./lianxi-keeper.js";
import { afterFinish, offerAfterFinish } from "./lianxi-finish-copy.js";
import { showRound } from "./lianxi-round-view.js";
import { handedOverLine } from "./xiezi-copy.js";
import type { DrillCharacter } from "./xiezi-drill-properties.js";
import type { DrillParts } from "./xiezi-view.js";

/**
 * The round of an Exercise 练习: each character's place, which is in the
 * square, and the move from one to the next.
 *
 * Apart from `lianxi-xiezi.ts`, which is the Exercise a block's controls
 * drive, so that the round can be about the places alone. The Exercise builds
 * the drill and the round together and hands the round a way to put a
 * character in the square.
 *
 * Each character completes its five passes at a stage before the next
 * character starts that stage. The completion animation plays before the
 * square changes. Visitors complete the allowed passes on each character
 * before the sign-up gate opens.
 */

/** One character of the Exercise, with its outlines, reading and recordings. */
export type ExerciseCharacter = Omit<DrillCharacter, "progress">;

/** What the round is built from. */
export interface ExerciseRoundProperties {
  readonly parts: Pick<DrillParts, "stage" | "square">;

  /** The row over the square, one item per character, in the same order. */
  readonly items: readonly HTMLElement[];
  readonly characters: readonly ExerciseCharacter[];

  /** Where each character starts, in the same order. The beginning where absent. */
  readonly places?: readonly DrillProgress[] | undefined;
  readonly keeper?: ExerciseKeeper | undefined;

  /** Put a character in the square, at the reader's place with it. */
  readonly show: (held: DrillCharacter) => void;

  /**
   * Called in place of showing a character the reader would write past the
   * taste a visitor is given (`src/handwriting/xiezi-taste.ts`). Absent for a
   * reader with an account.
   */
  readonly onTasted?: (() => void) | undefined;
}

/** The round over an Exercise's characters. */
export class ExerciseRound {
  readonly #properties: ExerciseRoundProperties;
  #progress: DrillProgress[];
  #current: number;
  #waiting = false;
  #transition: ReturnType<typeof setTimeout> | undefined;

  constructor(properties: ExerciseRoundProperties) {
    const { characters, places } = properties;

    this.#properties = properties;
    this.#progress = characters.map((_, at) => places?.[at] ?? drillStart());
    // The round starts at the first unfinished character, which the search
    // from the last one finds.
    this.#current = characters.length - 1;
  }

  /** Whether the pass is being saved or its completion animation is playing. */
  get waiting(): boolean {
    return this.#waiting;
  }

  /** Whether every character but the one in the square is finished. */
  othersFinished(): boolean {
    return this.#progress.every(
      (progress, at) => at === this.#current || isFinished(progress),
    );
  }

  /** Put the first unfinished character in the square. */
  start(): void {
    this.#moveOn();
  }

  /** Every character back at the beginning, once the keeper has been told. */
  again(): void {
    clearTimeout(this.#transition);
    void (this.#properties.keeper?.again() ?? Promise.resolve()).then(() => {
      this.#progress = this.#properties.characters.map(() => drillStart());
      this.#show(0);
    });
  }

  /**
   * A pass on the current character has ended.
   *
   * A pass in a stage that snaps is told to the keeper, and what the keeper
   * answers is adopted over the page's own arithmetic. A pass in the free
   * stage was marked by the marker, which is where a keeper keeps it.
   * @param progress - Where the character is now, by the page's arithmetic.
   */
  passEnded(progress: DrillProgress): void {
    const at = this.#current;
    const held = this.#properties.characters[at];
    const before = this.#progress[at];

    this.#waiting = true;
    this.#properties.parts.square.lock(true);
    settleAfterPass(
      held === undefined || before === undefined
        ? undefined
        : keptAfterPass(this.#properties.keeper, held.character, before),
      () => {
        this.#progress[at] = progress;
        this.#afterPass(before);
      },
      (places) => {
        this.#progress = [...places];
        this.#afterPass(before, true);
      },
    );
  }

  /** Keep writing this stage, then wait for its completion flash before moving. */
  #afterPass(before: DrillProgress | undefined, adopted = false): void {
    const progress = this.#progress[this.#current];
    const stageEnded =
      progress !== undefined &&
      before !== undefined &&
      progress.stage > before.stage;
    const tasted =
      this.#properties.onTasted !== undefined && !this.#tasted(this.#current);

    this.#showRound(this.#current);
    if (stageEnded || tasted) {
      this.#transition = setTimeout(() => this.#moveOn(), flashLength);
    } else if (adopted) {
      this.#show(this.#current);
    } else {
      this.#waiting = false;
      this.#properties.parts.square.lock(false);
    }
  }

  /**
   * Put the next unfinished character in the square, or say the Exercise is
   * done, or stop a visitor at the taste.
   */
  #moveOn(): void {
    const available = this.#progress.filter(
      (progress) =>
        !isFinished(progress) &&
        (this.#properties.onTasted === undefined || withinTaste(progress)),
    );
    const stage = Math.min(...available.map((progress) => progress.stage));
    const next = nextUnfinished(
      this.#progress,
      this.#current,
      (progress) =>
        isFinished(progress) ||
        progress.stage !== stage ||
        (this.#properties.onTasted !== undefined && !withinTaste(progress)),
    );
    const { onTasted } = this.#properties;

    if (
      next === undefined &&
      onTasted !== undefined &&
      this.#progress.some((progress) => !isFinished(progress))
    ) {
      onTasted();
    } else if (next === undefined) {
      this.#waiting = false;
      this.#showRound(undefined);
    } else {
      this.#show(next);
    }
  }

  /** Whether the character at `at` is still inside the taste. */
  #tasted(at: number): boolean {
    const progress = this.#progress[at];

    return progress === undefined || withinTaste(progress);
  }

  /** Put the character at `at` in the square, at the reader's place with it. */
  #show(at: number): void {
    const held = this.#properties.characters[at];
    const progress = this.#progress[at];

    this.#waiting = false;
    this.#current = at;

    if (held !== undefined && progress !== undefined) {
      this.#properties.show({ ...held, progress });
    }

    this.#showRound(at);
  }

  /** Say where the round has got to on the page. */
  #showRound(current: number | undefined): void {
    const { parts, items, keeper } = this.#properties;

    showRound(
      parts,
      items,
      this.#progress,
      current,
      afterFinish(handedOverLine, keeper),
    );

    if (current === undefined) {
      offerAfterFinish(parts.stage, keeper);
    }
  }
}
